// Settings → Suppliers tab.
//
// IMPORTANT: this is the SETTINGS tab — *connection management* only. The
// full catalogue browser lives at `apps/retailer/src/pages/suppliers/page.ts`
// (the main "Suppliers" page).
//
// Source-of-truth: /var/www/kitchenos/portal-standalone.html
//   tab button:           line 27036
//   renderSuppliersPanel: line 27091
//   SUPPLIERS const:      line 26659  (id, name, desc, bg)
//   kosGetSupplierAccounts / kosSetSupplierAccount / kosRemoveSupplierAccount
//                         line 26679-26691  (legacy localStorage state machine)
//
// Legacy state machine (which we mirror)
// --------------------------------------
//   none      → user enters account number, clicks "Request connection"
//   pending   → admin manually confirms with the supplier, then approves
//   connected → catalogue + discounts unlocked on the main Suppliers page
//
// In the legacy portal the user can only Add or Remove. We split that into
// the three actions the master spec asks for:
//   • "Request connection" (none → pending)
//   • "Cancel request"     (pending → none)
//   • "Disconnect"         (connected → none, via confirm dialog)
//
// Backend
// -------
//   GET    /api/portal/suppliers/connections
//   POST   /api/portal/suppliers/connections          { supplierId, accountNo }
//   DELETE /api/portal/suppliers/connections/:id
//
// None of these exist in kitchenos-api today (verified against server.js
// 2026-05). We:
//   1. Try GET first; on 404/501 we synthesise a "not connected" baseline
//      from the canonical 4-supplier seed (TKC / PWS / Omega / LDL — same
//      UUIDs the main Suppliers page uses).
//   2. Optimistically apply mutations. On 404/501 we toast "saved locally"
//      and persist to sessionStorage so a tab switch keeps the state.
//   3. On other errors we roll back and toast "save failed".

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";
import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./suppliers.css";

// ── Types ────────────────────────────────────────────────────────────────────

type ConnectionStatus = "connected" | "pending" | "none";

interface SupplierSeed {
  /** UUID matching the seeded supplier rows. */
  id: string;
  /** Short trade key — "TKC", "PWS", "Omega", "LDL". */
  key: string;
  /** Display name. */
  name: string;
  /** One-line catalogue description. */
  desc: string;
  /** Brand colour (hex). */
  colour: string;
  /** Logo glyph (1–3 chars). */
  logo: string;
}

interface Connection {
  supplierId: string;
  status: ConnectionStatus;
  /** Trade account number — required to request a connection. */
  accountNo?: string;
  /** Epoch ms when the user submitted the request. */
  submittedAt?: number;
  /** Epoch ms when an admin confirmed the connection. */
  connectedSince?: number;
}

interface ServerConnection {
  supplierId?: string;
  id?: string;
  status?: string;
  accountNo?: string;
  submittedAt?: number;
  connectedSince?: number;
  verified?: number;
  since?: number;
}

interface ServerConnectionsResponse {
  connections?: ServerConnection[];
}

interface RequestPayload {
  supplierId: string;
  accountNo: string;
}

interface RequestResponse {
  connection?: ServerConnection;
}

// ── Seed (mirrors SUPPLIER_RANGES UUIDs in apps/retailer/src/pages/suppliers/page.ts) ──

const SUPPLIER_SEED: readonly SupplierSeed[] = [
  {
    id: "00000000-0000-0000-0000-000000000001",
    key: "TKC",
    name: "TKC Components",
    desc: "Carcasses, trim, handles",
    colour: "#1E40AF",
    logo: "TKC",
  },
  {
    id: "00000000-0000-0000-0000-000000000002",
    key: "PWS",
    name: "PWS",
    desc: "Doors, frontals, accessories",
    colour: "#DC2626",
    logo: "PWS",
  },
  {
    id: "00000000-0000-0000-0000-000000000003",
    key: "Omega",
    name: "Omega Kitchens",
    desc: "In-frame painted doors",
    colour: "#1E3A8A",
    logo: "Ω",
  },
  {
    id: "00000000-0000-0000-0000-000000000004",
    key: "LDL",
    name: "LDL Online",
    desc: "Worktops, splashbacks",
    colour: "#F59E0B",
    logo: "LDL",
  },
];

const STORAGE_KEY = "tkos.set.supplierConnections";

// ── Helpers ──────────────────────────────────────────────────────────────────

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 501);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function normaliseStatus(raw: string | undefined): ConnectionStatus {
  if (raw === "connected") return "connected";
  if (raw === "pending") return "pending";
  return "none";
}

function fromServerConn(s: ServerConnection): Connection {
  const id = s.supplierId ?? s.id ?? "";
  return {
    supplierId: id,
    status: normaliseStatus(s.status),
    ...(s.accountNo ? { accountNo: s.accountNo } : {}),
    ...(typeof s.submittedAt === "number" ? { submittedAt: s.submittedAt } : {}),
    ...(typeof (s.connectedSince ?? s.since ?? s.verified) === "number"
      ? { connectedSince: (s.connectedSince ?? s.since ?? s.verified) as number }
      : {}),
  };
}

function readSession(): Map<string, Connection> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as Connection[];
    return new Map(parsed.map((c) => [c.supplierId, c]));
  } catch {
    return new Map();
  }
}

function writeSession(connections: Map<string, Connection>): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...connections.values()]));
  } catch {
    /* ignore quota errors */
  }
}

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

function formatSince(epoch: number): string {
  return DATE_FMT.format(new Date(epoch));
}

// ── Render helpers ───────────────────────────────────────────────────────────

function statusBadge(status: ConnectionStatus): string {
  if (status === "connected")
    return `<span class="set-sup-badge set-sup-badge--ok">Connected</span>`;
  if (status === "pending")
    return `<span class="set-sup-badge set-sup-badge--pending">Pending</span>`;
  return `<span class="set-sup-badge set-sup-badge--none">Not connected</span>`;
}

function actionMarkup(s: SupplierSeed, conn: Connection): string {
  if (conn.status === "connected") {
    return `<button type="button" class="set-btn set-btn-danger set-sup-action" data-action="disconnect" data-id="${escapeHtml(s.id)}">Disconnect</button>`;
  }
  if (conn.status === "pending") {
    return `<button type="button" class="set-btn set-btn-secondary set-sup-action" data-action="cancel" data-id="${escapeHtml(s.id)}">Cancel request</button>`;
  }
  return `
    <div class="set-sup-request">
      <input
        class="set-input set-sup-input"
        type="text"
        inputmode="text"
        placeholder="Trade account #"
        aria-label="Trade account number for ${escapeHtml(s.name)}"
        data-input-for="${escapeHtml(s.id)}">
      <button type="button" class="set-btn set-sup-action" data-action="request" data-id="${escapeHtml(s.id)}">Request connection</button>
    </div>
  `;
}

function renderRow(s: SupplierSeed, conn: Connection): string {
  const sinceLine =
    conn.status === "connected" && typeof conn.connectedSince === "number"
      ? `<div class="set-sup-since">Connected since ${escapeHtml(formatSince(conn.connectedSince))}</div>`
      : conn.status === "pending" && typeof conn.submittedAt === "number"
      ? `<div class="set-sup-since">Requested ${escapeHtml(formatSince(conn.submittedAt))}${conn.accountNo ? ` · acc. ${escapeHtml(conn.accountNo)}` : ""}</div>`
      : "";
  return `
    <li class="set-sup-row" data-row-for="${escapeHtml(s.id)}" data-status="${conn.status}">
      <div class="set-sup-logo" style="background:${escapeHtml(s.colour)}" aria-hidden="true">${escapeHtml(s.logo)}</div>
      <div class="set-sup-meta">
        <div class="set-sup-name">${escapeHtml(s.name)}</div>
        <div class="set-sup-desc">${escapeHtml(s.desc)}</div>
        ${sinceLine}
      </div>
      <div class="set-sup-status">${statusBadge(conn.status)}</div>
      <div class="set-sup-actions">${actionMarkup(s, conn)}</div>
    </li>
  `;
}

function renderShell(container: HTMLElement): {
  bannerEl: HTMLElement;
  listEl: HTMLElement;
  discoverEl: HTMLButtonElement;
  dialogEl: HTMLDialogElement;
  dialogTitleEl: HTMLElement;
  dialogBodyEl: HTMLElement;
  dialogConfirmBtn: HTMLButtonElement;
  dialogCancelBtn: HTMLButtonElement;
  wizardEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="set-sup-root" aria-labelledby="set-sup-title">
      <header class="set-sup-head">
        <h2 class="set-section-title" id="set-sup-title">Supplier Connections</h2>
        <p class="set-sup-help">
          Manage which suppliers you're connected to. Connected suppliers'
          catalogues and discounts appear on the Suppliers page.
        </p>
      </header>

      <div class="set-sup-banner" data-role="banner" hidden></div>

      <ul class="set-sup-list" data-role="list" aria-label="Supplier connections"></ul>

      <div class="set-sup-discover">
        <button type="button" class="set-sup-discover-btn" data-role="discover">
          <span aria-hidden="true">＋</span> Discover more suppliers
        </button>
      </div>

      <!-- Which suppliers' door ranges customers are shown in the lead-gen
           wizard. Read-only: currently curated centrally by Kitchengine. The
           endpoint and data model already support a retailer editing this, so
           this becomes interactive without a schema change. -->
      <section class="set-sup-wizard" aria-labelledby="set-sup-wizard-title" data-role="wizard-suppliers">
        <header class="set-sup-head">
          <h2 class="set-section-title" id="set-sup-wizard-title">Door ranges in your enquiry wizard</h2>
          <p class="set-sup-help">
            The door ranges your customers can choose from at step 2 of your
            enquiry wizard. Managed for you — ask us to change your selection.
          </p>
        </header>
        <div class="set-sup-wizard-body">Loading…</div>
      </section>

      <dialog class="set-sup-dialog" data-role="dialog" aria-labelledby="set-sup-dialog-title">
        <form method="dialog" class="set-sup-dialog-form">
          <h3 class="set-sup-dialog-title" id="set-sup-dialog-title" data-role="dialog-title"></h3>
          <p class="set-sup-dialog-body" data-role="dialog-body"></p>
          <div class="set-sup-dialog-actions">
            <button type="button" class="set-btn set-btn-secondary" data-role="dialog-cancel" value="cancel">Cancel</button>
            <button type="button" class="set-btn set-btn-danger" data-role="dialog-confirm" value="confirm">Confirm</button>
          </div>
        </form>
      </dialog>
    </section>
  `;

  return {
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    listEl: container.querySelector<HTMLElement>("[data-role='list']")!,
    discoverEl: container.querySelector<HTMLButtonElement>("[data-role='discover']")!,
    dialogEl: container.querySelector<HTMLDialogElement>("[data-role='dialog']")!,
    dialogTitleEl: container.querySelector<HTMLElement>("[data-role='dialog-title']")!,
    dialogBodyEl: container.querySelector<HTMLElement>("[data-role='dialog-body']")!,
    dialogConfirmBtn: container.querySelector<HTMLButtonElement>("[data-role='dialog-confirm']")!,
    dialogCancelBtn: container.querySelector<HTMLButtonElement>("[data-role='dialog-cancel']")!,
    wizardEl: container.querySelector<HTMLElement>("[data-role='wizard-suppliers'] .set-sup-wizard-body")!,
  };
}

interface WizardSupplier { slug: string; name: string; doorRangeCount: number }
interface WizardSuppliersResponse {
  ok: boolean;
  /** true = no selection made, so the wizard shows every published range. */
  unrestricted: boolean;
  suppliers: WizardSupplier[];
}

function renderWizardSuppliers(el: HTMLElement, data: WizardSuppliersResponse | null): void {
  if (!data) {
    el.innerHTML = `<p class="set-sup-help">Couldn't load your wizard door ranges just now.</p>`;
    return;
  }
  if (!data.suppliers.length) {
    el.innerHTML = `<p class="set-sup-help">No door ranges are available yet.</p>`;
    return;
  }
  const chips = data.suppliers.map((s) =>
    `<li class="set-sup-wizard-chip">${escapeHtml(s.name)}
       <span class="set-sup-wizard-count">${s.doorRangeCount} ${s.doorRangeCount === 1 ? "range" : "ranges"}</span>
     </li>`).join("");
  // Say which of the two states this is — "showing everything available" and
  // "showing a chosen shortlist" look identical otherwise.
  const note = data.unrestricted
    ? `<p class="set-sup-help">You're currently showing <strong>all</strong> available door ranges.</p>`
    : `<p class="set-sup-help">Your wizard shows <strong>only</strong> the ranges below.</p>`;
  el.innerHTML = `${note}<ul class="set-sup-wizard-list">${chips}</ul>`;
}

function renderBanner(el: HTMLElement, msg: string | null, kind: "info" | "error"): void {
  if (!msg) {
    el.hidden = true;
    el.textContent = "";
    el.classList.remove("set-sup-banner--error");
    return;
  }
  el.hidden = false;
  el.textContent = msg;
  el.classList.toggle("set-sup-banner--error", kind === "error");
}

function renderList(listEl: HTMLElement, connections: Map<string, Connection>): void {
  listEl.innerHTML = SUPPLIER_SEED.map((s) => {
    const conn = connections.get(s.id) ?? { supplierId: s.id, status: "none" as const };
    return renderRow(s, conn);
  }).join("");
}

// ── Mount ────────────────────────────────────────────────────────────────────

const tab: SettingsTab = {
  id: "suppliers",
  label: "Suppliers",
  icon: iconMarkup("package", { size: "sm" }),

  mount(container: HTMLElement, ctx: PageContext) {
    const ui = renderShell(container);
    let controller: AbortController | null = new AbortController();
    let disposed = false;
    let isLocalMode = false;
    let connections = new Map<string, Connection>();

    // Always start with seed defaults so the list renders before fetch resolves.
    renderList(ui.listEl, connections);

    // Wizard door-range selection — independent of the connection list above,
    // so a failure here leaves the rest of the tab working.
    void (async () => {
      try {
        const resp = await ctx.api<WizardSuppliersResponse>("/portal/wizard-suppliers", {
          signal: controller?.signal,
        });
        if (!disposed) renderWizardSuppliers(ui.wizardEl, resp);
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        renderWizardSuppliers(ui.wizardEl, null);
      }
    })();

    // ── Dialog plumbing ─────────────────────────────────────────────────────

    let dialogResolve: ((confirmed: boolean) => void) | null = null;

    const closeDialog = (confirmed: boolean): void => {
      if (ui.dialogEl.open) ui.dialogEl.close();
      if (dialogResolve) {
        dialogResolve(confirmed);
        dialogResolve = null;
      }
    };

    const onConfirmClick = (): void => closeDialog(true);
    const onCancelClick = (): void => closeDialog(false);
    const onDialogCancel = (e: Event): void => {
      e.preventDefault();
      closeDialog(false);
    };

    ui.dialogConfirmBtn.addEventListener("click", onConfirmClick);
    ui.dialogCancelBtn.addEventListener("click", onCancelClick);
    ui.dialogEl.addEventListener("cancel", onDialogCancel);

    const confirmAction = (title: string, body: string, confirmLabel: string): Promise<boolean> => {
      ui.dialogTitleEl.textContent = title;
      ui.dialogBodyEl.textContent = body;
      ui.dialogConfirmBtn.textContent = confirmLabel;
      try {
        ui.dialogEl.showModal();
      } catch {
        // showModal can throw if the dialog is already open — close + retry.
        ui.dialogEl.close();
        ui.dialogEl.showModal();
      }
      return new Promise<boolean>((resolve) => {
        dialogResolve = resolve;
      });
    };

    // ── Network helpers ─────────────────────────────────────────────────────

    const writeStateThrough = (): void => {
      if (isLocalMode) writeSession(connections);
    };

    const requestConnection = async (
      supplierId: string,
      accountNo: string,
    ): Promise<void> => {
      const prev = connections.get(supplierId);
      const optimistic: Connection = {
        supplierId,
        status: "pending",
        accountNo,
        submittedAt: Date.now(),
      };
      connections.set(supplierId, optimistic);
      renderList(ui.listEl, connections);

      if (isLocalMode) {
        writeSession(connections);
        ctx.showToast("Request saved locally — admin will verify shortly", "info");
        return;
      }

      try {
        const reqController = new AbortController();
        const payload: RequestPayload = { supplierId, accountNo };
        const res = await ctx.api<RequestResponse>(
          "/portal/suppliers/connections",
          {
            method: "POST",
            body: JSON.stringify(payload),
            signal: reqController.signal,
          },
        );
        if (disposed) return;
        if (res.connection) {
          connections.set(supplierId, fromServerConn(res.connection));
          renderList(ui.listEl, connections);
        }
        ctx.showToast("Connection requested — pending admin verification", "success");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          isLocalMode = true;
          writeSession(connections);
          ctx.showToast("Saved locally — server endpoint pending", "info");
          return;
        }
        // Roll back on real error.
        if (prev) connections.set(supplierId, prev);
        else connections.delete(supplierId);
        renderList(ui.listEl, connections);
        const msg = err instanceof Error ? err.message : "Couldn't request connection";
        ctx.showToast(msg, "error");
      }
    };

    const removeConnection = async (supplierId: string): Promise<void> => {
      const prev = connections.get(supplierId);
      connections.delete(supplierId);
      renderList(ui.listEl, connections);

      if (isLocalMode) {
        writeSession(connections);
        ctx.showToast("Removed locally", "info");
        return;
      }

      try {
        const reqController = new AbortController();
        await ctx.api<unknown>(
          `/portal/suppliers/connections/${encodeURIComponent(supplierId)}`,
          { method: "DELETE", signal: reqController.signal },
        );
        if (disposed) return;
        ctx.showToast("Connection removed", "success");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          isLocalMode = true;
          writeSession(connections);
          ctx.showToast("Removed locally — server endpoint pending", "info");
          return;
        }
        if (prev) connections.set(supplierId, prev);
        renderList(ui.listEl, connections);
        const msg = err instanceof Error ? err.message : "Couldn't remove connection";
        ctx.showToast(msg, "error");
      }
    };

    // ── Initial fetch ───────────────────────────────────────────────────────

    const load = async (): Promise<void> => {
      try {
        const data = await ctx.api<ServerConnectionsResponse>(
          "/portal/suppliers/connections",
          { signal: controller!.signal },
        );
        if (disposed) return;
        const next = new Map<string, Connection>();
        for (const c of data.connections ?? []) {
          const norm = fromServerConn(c);
          if (norm.supplierId) next.set(norm.supplierId, norm);
        }
        connections = next;
        isLocalMode = false;
        renderList(ui.listEl, connections);
        renderBanner(ui.bannerEl, null, "info");
      } catch (err) {
        if (isAbortError(err) || disposed) return;
        if (isMissingEndpoint(err)) {
          // Hydrate from session if we have prior local edits.
          isLocalMode = true;
          connections = readSession();
          renderList(ui.listEl, connections);
          renderBanner(
            ui.bannerEl,
            "Server endpoint pending — connection state is kept locally for this session.",
            "info",
          );
          return;
        }
        const msg = err instanceof Error ? err.message : "Couldn't load connections";
        renderBanner(ui.bannerEl, msg, "error");
        ctx.showToast(msg, "error");
      }
    };

    void load();

    // ── Click delegation ────────────────────────────────────────────────────

    const onListClick = async (e: MouseEvent): Promise<void> => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const btn = target.closest<HTMLButtonElement>(".set-sup-action");
      if (!btn) return;
      const action = btn.dataset.action;
      const id = btn.dataset.id;
      if (!action || !id) return;
      const supplier = SUPPLIER_SEED.find((s) => s.id === id);
      if (!supplier) return;

      if (action === "request") {
        const input = ui.listEl.querySelector<HTMLInputElement>(
          `input[data-input-for="${CSS.escape(id)}"]`,
        );
        const accountNo = input?.value.trim() ?? "";
        if (!accountNo) {
          ctx.showToast("Enter your trade account number first", "error");
          input?.focus();
          return;
        }
        await requestConnection(id, accountNo);
        writeStateThrough();
      } else if (action === "cancel") {
        const ok = await confirmAction(
          "Cancel request?",
          `Cancel your pending connection request to ${supplier.name}? You can resubmit later.`,
          "Cancel request",
        );
        if (ok) {
          await removeConnection(id);
          writeStateThrough();
        }
      } else if (action === "disconnect") {
        const ok = await confirmAction(
          "Disconnect supplier?",
          `Disconnect from ${supplier.name}? Their catalogue and negotiated discounts will no longer appear on the Suppliers page.`,
          "Disconnect",
        );
        if (ok) {
          await removeConnection(id);
          writeStateThrough();
        }
      }
    };

    ui.listEl.addEventListener("click", onListClick);

    // ── Discover-more stub ──────────────────────────────────────────────────

    const onDiscoverClick = (): void => {
      ctx.showToast("Coming soon — supplier directory", "info");
    };
    ui.discoverEl.addEventListener("click", onDiscoverClick);

    // ── Cleanup ─────────────────────────────────────────────────────────────

    return () => {
      disposed = true;
      controller?.abort();
      controller = null;
      ui.listEl.removeEventListener("click", onListClick);
      ui.discoverEl.removeEventListener("click", onDiscoverClick);
      ui.dialogConfirmBtn.removeEventListener("click", onConfirmClick);
      ui.dialogCancelBtn.removeEventListener("click", onCancelClick);
      ui.dialogEl.removeEventListener("cancel", onDialogCancel);
      if (ui.dialogEl.open) ui.dialogEl.close();
      if (dialogResolve) {
        dialogResolve(false);
        dialogResolve = null;
      }
    };
  },
};

export default tab;
